import React from 'react';

import { RiskTag } from '@/components/chariot-ui';
import { Checkbox } from '@/components/form/Checkbox';
import { StatusChip, StatusChipVariant } from '@/components/ui/status-chip';
import { ExportSectionHeader } from '@/sections/export/components/ExportSectionHeader';
import type { Severity } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

export type ExportOption = {
  label: string;
  value: string | number;
  severity?: Severity;
  variant?: StatusChipVariant;
  icon?: React.ReactNode;
  count?: number | string;
};

export function ExportSection({
  label,
  options,
  styleType = 'table',
  selected,
  setSelected,
}: {
  label: string;
  options: ExportOption[];
  styleType?: 'table' | 'columns';
  selected: ExportOption[];
  setSelected: (options: ExportOption[]) => void;
}) {
  const selectedValues = selected.map(o => o.value);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`export-section-${label}`}
    >
      <ExportSectionHeader
        selectAll={selectedValues.length === options.length}
        onToggleSelectAll={(selectedAll: boolean) => {
          if (selectedAll) {
            setSelected(options);
          } else {
            setSelected([]);
          }
        }}
        label={label}
      />
      <div
        className={cn(
          'p-4 pt-2',
          styleType === 'columns' && 'grid grid-cols-2 gap-2'
        )}
        data-testid="export-section-options"
      >
        {options.map(option => {
          return (
            <div
              key={option.value}
              className={cn(
                'flex items-center justify-between',
                styleType === 'table' &&
                  'border-chariot-stroke border-b py-2.5 last:border-b-0'
              )}
            >
              <Checkbox
                name={`select-${option.value}`}
                label={
                  option.severity ? (
                    <RiskTag
                      severity={option.severity}
                      size="xs"
                      className="py-0"
                    />
                  ) : option.variant ? (
                    <StatusChip
                      label={option.label}
                      variant={option.variant}
                      icon={option.icon}
                      className="py-0"
                    />
                  ) : (
                    option.label
                  )
                }
                value={selectedValues.includes(option.value) ? 'true' : 'false'}
                onChange={() => {
                  if (selectedValues.includes(option.value)) {
                    setSelected(selected.filter(o => o.value !== option.value));
                  } else {
                    setSelected([...selected, option]);
                  }
                }}
                showTick={true}
              />
              <p className="text-chariot-text-disabled text-sm">
                {option.count}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
